import { Button } from "@/components/ui/baseUi/button"
import { CalendarIcon } from "@images/svgIcons/calendarIcon"
import { GroupIcon } from "@images/svgIcons/groupIcon"
import { MessagesIcon } from "@images/svgIcons/messagesIcon"
import { MoneyIcon } from "@images/svgIcons/moneyIcon"
import { FiveIcon } from "@images/svgIcons/numbers/fiveIcon"
import { FourIcon } from "@images/svgIcons/numbers/fourIcon"
import { OneIcon } from "@images/svgIcons/numbers/oneIcon"
import { SixIcon } from "@images/svgIcons/numbers/sixIcon"
import { ThreeIcon } from "@images/svgIcons/numbers/threeIcon"
import { TwoIcon } from "@images/svgIcons/numbers/twoIcon"
import { ProductsIcon } from "@images/svgIcons/productsIcon"
import { UnionSmallIcon } from "@images/svgIcons/unionSmallIcon"
import { word } from "@modules/core/utils/i18n"
import { useState } from "react"
import styled from "styled-components"
import { Text } from "@/components/ui/baseUi/text"

type WP = {
  onClick: () => void
}

export const BoxFormWelcomePage = ({ onClick }: WP) => {
  const [isSummary, setIsSummary] = useState(false)

  return (
    <>
      <div className="flex flex-col items-center">
        <UnionSmallIcon color="var(--color-white)" />
        <Text fontStyle="DM" className="mt-3 mb-2">
          {isSummary ? word("box.creation.welcome.summary.title") : word("box.creation.welcome.title")}
        </Text>
        <Text fontStyle="HS" className="text-neutral-600">
          {isSummary ? word("box.creation.welcome.summary.description") : word("box.creation.welcome.description")}
        </Text>
      </div>

      {isSummary ? <WelcomeSummaryContainer /> : <WelcomeCardsContainer />}

      <Button size="lg" variant="primary" className="mb-5" onClick={() => (isSummary ? onClick() : setIsSummary(true))}>
        {isSummary ? word("box.creation.welcome.button.create") : word("box.creation.welcome.button.start")}
      </Button>
    </>
  )
}

type InformationCardProps = {
  key: "PLANNING" | "OFFERS" | "MEMBERS" | "FINANCE" | "CHAT"
  icon: React.ReactNode
}

const WelcomeCardsContainer = () => {
  const informationCards: InformationCardProps[] = [
    { key: "PLANNING", icon: <CalendarIcon color="var(--color-white)" /> },
    { key: "OFFERS", icon: <ProductsIcon color="var(--color-white)" /> },
    { key: "MEMBERS", icon: <GroupIcon color="var(--color-white)" /> },
    { key: "FINANCE", icon: <MoneyIcon color="var(--color-white)" /> },
    { key: "CHAT", icon: <MessagesIcon color="var(--color-white)" /> },
  ]
  return (
    <div className="flex flex-wrap justify-center gap-8">
      {informationCards.map(({ key, icon }) => (
        <div key={key} className="w-[13.3rem] rounded-3xl border border-neutral-200 bg-black p-5">
          <div className="mb-3 flex items-center gap-3">
            {icon}
            <Text fontStyle="TL">{word(`box.creation.welcome.infos.${key}.title`)}</Text>
          </div>

          <div className="flex flex-col gap-3">
            <Text fontStyle="BM" className="text-neutral-800">
              {word(`box.creation.welcome.infos.${key}.description.1`)}
            </Text>
            <Text fontStyle="BM" className="text-neutral-800">
              {word(`box.creation.welcome.infos.${key}.description.2`)}
            </Text>
          </div>
        </div>
      ))}
    </div>
  )
}

type SummaryProps = {
  step: "1" | "2" | "3" | "4" | "5" | "6"
  icon: React.ReactNode
  top: string
  widthRem?: number
}

const WelcomeSummaryContainer = () => {
  const steps: SummaryProps[] = [
    { step: "1", icon: <OneIcon color="var(--primary-200)" />, top: "97px" },
    { step: "2", icon: <TwoIcon color="var(--primary-200)" />, top: "25px" },
    { step: "3", icon: <ThreeIcon color="var(--primary-200)" />, top: "140px" },
    { step: "4", icon: <FourIcon color="var(--primary-200)" />, top: "25px" },
    { step: "5", icon: <FiveIcon color="var(--primary-200)" />, top: "94px", widthRem: 10 },
    { step: "6", icon: <SixIcon color="var(--primary-200)" />, top: "-19px" },
  ]
  return (
    <StyledSummaryContainer className="flex w-full flex-wrap justify-center gap-6 p-3 md:p-5">
      {steps.map(({ step, icon, top, widthRem = 9 }) => (
        <StyledSummaryStep key={step} $top={top}>
          {icon}
          <Text
            fontStyle="HM"
            className="absolute text-left"
            style={{ left: "2.5rem", top: "1.875rem", width: `${widthRem}rem` }}
          >
            {word(`box.creation.welcome.summary.step.${step}`)}
          </Text>
        </StyledSummaryStep>
      ))}
    </StyledSummaryContainer>
  )
}

const StyledSummaryContainer = styled.div`
  margin-bottom: 11rem;
  column-gap: 8rem;
  @media screen and (max-width: 1360px) {
    margin-bottom: 4rem;
  }
`

const StyledSummaryStep = styled.div<{ $top: string }>`
  position: relative;
  top: ${({ $top }) => $top};
  @media screen and (max-width: 1360px) {
    top: unset;
  }
`
